// src/pages/supervisor/AgentsPerformance.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Row,
  Table,
  Badge,
  ProgressBar,
  ButtonGroup,
  Spinner,
  Dropdown,
  Container,
  Alert,
  Form,
  InputGroup,
  Modal,
  Placeholder
} from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import {
  Person,
  BarChart,
  TrendingUp,
  ArrowDown,
  Award,
  Trophy,
  Clock,
  Calendar,
  Eye,
  Download,
  FilePdf,
  FileEarmarkExcel,
  Printer,
  Search,
  Filter,
  ArrowUp,
  Star,
  StarFill,
  ChevronLeft,
  ChevronRight,
  CircleFill,
  ExclamationCircle,
  Repeat,
  Cash,
  Bullseye,
  GraphUp,
  People,
  Shop,
  Telephone,
  Envelope,
  GeoAlt,
  Lock,
  Shield,
  ArrowLeft,
  Building,
  Globe
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";

export default function AgentsPerformance() {
  const navigate = useNavigate();
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'XOF';
  
  // ✅ Récupérer les infos de l'utilisateur connecté
  const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
  const userRoleId = userData?.role_id || null;
  const userMarcheId = userData?.id_marche || null;
  const hasMarket = !!userMarcheId;

  // ✅ Déterminer les rôles - CORRIGÉ
  const isAdminEntite = userRoleId === 1002;           // Admin Entité (hors marché)
  const isAdminPrincipal = userRoleId === 1003;        // Admin Principal (marchés)
  const isSuperAdmin = userRoleId === 1001;
  const isAdminMarche = userRoleId === 1005;
  const isSuperviseur = userRoleId === 1007;
  const isAgent = userRoleId === 1012;
  const isCollecteur = userRoleId === 1013;

  // ✅ Vérifier si l'utilisateur peut voir cette page
  const canViewPerformance = isSuperAdmin || isAdminEntite || isAdminPrincipal || isAdminMarche || isSuperviseur;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState("month");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("collecte");
  const [sortDirection, setSortDirection] = useState("desc");

  const getAuthToken = () => sessionStorage.getItem("authToken");
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + defaultCurrency;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) { return dateString; }
  };

  // ✅ Configuration des badges de rôle - CORRIGÉ
  const getRoleConfig = (roleId) => {
    const roleConfigs = {
      1001: { label: "Super Admin", color: "danger", icon: <Shield className="me-1" size={12} /> },
      1002: { label: "Admin Entité", color: "primary", icon: <Building className="me-1" size={12} /> },
      1003: { label: "Admin Principal", color: "danger", icon: <Globe className="me-1" size={12} /> },
      1005: { label: "Admin Marché", color: "primary", icon: <Shop className="me-1" size={12} /> },
      1007: { label: "Superviseur", color: "info", icon: <Eye className="me-1" size={12} /> },
      1012: { label: "Agent", color: "success", icon: <Person className="me-1" size={12} /> },
      1013: { label: "Collecteur", color: "warning", icon: <Person className="me-1" size={12} /> }
    };
    return roleConfigs[roleId] || { label: "Agent", color: "secondary", icon: <Person className="me-1" size={12} /> };
  };

  const loadData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        time_range: timeRange
      };

      const response = await axios.get(`${API_BASE_URL}/supervisor/agents-performance`, {
        headers: getHeaders(),
        params
      });

      if (response.data.success) {
        setAgents(response.data.agents || []);
        setStats(response.data.stats || null);
      } else {
        setError(response.data.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error("Erreur chargement performance agents:", err);
      if (err.response?.status === 403) {
        setError("Accès non autorisé. Vous n'avez pas les droits pour voir la performance des agents.");
      } else {
        setError(err.response?.data?.message || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    if (canViewPerformance) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [timeRange, canViewPerformance]);

  // Tri et filtrage des agents
  const filteredAndSortedAgents = useMemo(() => {
    let filtered = agents;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(agent =>
        agent.nom?.toLowerCase().includes(term) ||
        agent.email?.toLowerCase().includes(term) ||
        agent.telephone?.includes(term)
      );
    }

    return filtered.sort((a, b) => {
      let aVal = a[sortField] || 0;
      let bVal = b[sortField] || 0;
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [agents, searchTerm, sortField, sortDirection]);

  // Statistiques calculées
  const computedStats = useMemo(() => {
    if (!agents.length) return null;

    const totalCollecte = agents.reduce((sum, a) => sum + (a.collecte || 0), 0);
    const totalObjectif = agents.reduce((sum, a) => sum + (a.objectif || 0), 0);
    const moyennePerformance = agents.length > 0 
      ? agents.reduce((sum, a) => sum + (a.performance || 0), 0) / agents.length 
      : 0;
    const meilleurAgent = agents.reduce((a, b) => (a.performance || 0) > (b.performance || 0) ? a : b, agents[0] || null);
    const meilleurCollecteur = agents.reduce((a, b) => (a.collecte || 0) > (b.collecte || 0) ? a : b, agents[0] || null);

    return {
      totalCollecte,
      totalObjectif,
      moyennePerformance: Math.round(moyennePerformance),
      meilleurAgent,
      meilleurCollecteur,
      totalAgents: agents.length,
      tauxRecouvrement: totalObjectif > 0 ? Math.round((totalCollecte / totalObjectif) * 100) : 0
    };
  }, [agents]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setShowAgentModal(true);
  };

  // ✅ Configuration du graphique - Ne prendre que les 6 premiers agents
  const chartOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      height: 400,
      toolbar: { show: true, tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      background: 'transparent',
      animations: { enabled: true, easing: 'easeinout', speed: 800 }
    },
    colors: ['#2ecc71', '#9b59b6'],
    plotOptions: {
      bar: {
        borderRadius: 0,
        horizontal: false,
        columnWidth: '50%',
        dataLabels: { position: 'top' }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val.toLocaleString('fr-FR'),
      offsetY: -20,
      style: { fontSize: '10px', fontWeight: 'bold', colors: ['#2c3e50'] }
    },
    xaxis: {
      categories: agents.slice(0, 6).map(a => a.nom || 'Agent') || [],
      labels: {
        style: { colors: '#2c3e50', fontSize: '12px', fontWeight: 600 },
        rotate: 0
      }
    },
    yaxis: {
      labels: {
        formatter: (value) => value.toLocaleString('fr-FR'),
        style: { colors: '#6c757d', fontSize: '11px' }
      },
      title: {
        text: `Montant (${defaultCurrency})`,
        style: { fontSize: '12px', fontWeight: 600, color: '#2c3e50' }
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '13px',
      fontWeight: 600,
      markers: { width: 14, height: 14, radius: 4 }
    },
    tooltip: {
      y: {
        formatter: (value) => `${value.toLocaleString('fr-FR')} ${defaultCurrency}`,
        title: { formatter: (seriesName) => `<strong>${seriesName}</strong>` }
      }
    },
    grid: {
      borderColor: '#e0e6ed',
      strokeDashArray: 5,
      padding: { top: 20 }
    }
  }), [agents, defaultCurrency]);

  const chartSeries = useMemo(() => [
    { name: 'Collecté', data: agents.slice(0, 6).map(a => a.collecte || 0) },
    { name: 'Objectif', data: agents.slice(0, 6).map(a => a.objectif || 0) }
  ], [agents]);

  // ============================================================
  // SKELETON COMPONENTS
  // ============================================================

  const SkeletonStatCard = () => (
    <Card className="border-0 shadow-sm h-100 stats-card">
      <div className="position-absolute top-0 start-0 w-100" style={{ height: '4px', backgroundColor: '#e9ecef' }} />
      <Card.Body className="p-4">
        <div className="d-flex align-items-center">
          <div className="rounded-3 p-2 me-3" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
            <div className="rounded-circle" style={{ width: '24px', height: '24px', backgroundColor: '#e9ecef' }} />
          </div>
          <div className="flex-grow-1">
            <Placeholder as="div" animation="glow">
              <Placeholder xs={8} style={{ height: '28px', display: 'block' }} className="mb-1" />
              <Placeholder xs={6} style={{ height: '14px', display: 'block' }} />
            </Placeholder>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const SkeletonPodium = () => (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white border-bottom py-3">
        <Placeholder as="div" animation="glow">
          <Placeholder xs={3} style={{ height: '20px' }} />
        </Placeholder>
      </Card.Header>
      <Card.Body>
        <Row className="align-items-end">
          {[1, 2, 3].map((item) => (
            <Col key={item} md={4} className="text-center">
              <div className="p-3 bg-light rounded-3 border">
                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto mb-2" style={{ width: '48px', height: '48px' }}>
                  <Placeholder as="div" animation="glow" style={{ width: '24px', height: '24px' }} />
                </div>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={6} style={{ height: '14px', display: 'block', margin: '0 auto' }} className="mb-1" />
                  <Placeholder xs={4} style={{ height: '12px', display: 'block', margin: '0 auto' }} className="mb-2" />
                  <Placeholder xs={8} style={{ height: '20px', display: 'block', margin: '0 auto' }} className="mb-2" />
                  <Placeholder xs={10} style={{ height: '6px', display: 'block', margin: '0 auto' }} />
                </Placeholder>
              </div>
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  );

  const SkeletonChart = () => (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white border-bottom py-3">
        <div className="d-flex justify-content-between align-items-center">
          <Placeholder as="div" animation="glow">
            <Placeholder xs={4} style={{ height: '20px' }} />
            <Placeholder xs={3} style={{ height: '14px' }} className="mt-1" />
          </Placeholder>
          <Placeholder as="div" animation="glow">
            <Placeholder xs={6} style={{ height: '38px' }} />
          </Placeholder>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="bg-light rounded-3" style={{ height: '400px', position: 'relative', overflow: 'hidden' }}>
          <div className="w-100 h-100 d-flex align-items-center justify-content-center">
            <Spinner animation="border" variant="secondary" size="sm" className="me-2" />
            <span className="text-muted small">Chargement...</span>
          </div>
          <div className="position-absolute bottom-0 start-0 w-100 d-flex justify-content-around px-3" style={{ height: '60%', alignItems: 'flex-end' }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-light rounded-top" style={{ 
                width: '40px', 
                height: `${20 + Math.random() * 60}%`,
                backgroundColor: '#e9ecef',
                borderRadius: '4px 4px 0 0'
              }} />
            ))}
          </div>
        </div>
        <Placeholder as="div" animation="glow" className="mt-3">
          <Placeholder xs={4} style={{ height: '16px' }} className="mx-auto d-block" />
        </Placeholder>
      </Card.Body>
    </Card>
  );

  const SkeletonTable = () => (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-0">
        <div className="table-responsive">
          <table className="table mb-0">
            <thead className="bg-light">
              <tr>
                {[...Array(7)].map((_, i) => (
                  <th key={i} className="py-3">
                    <Placeholder as="span" animation="glow" xs={i === 0 ? 2 : i === 1 ? 6 : i === 2 ? 4 : i === 3 ? 4 : i === 4 ? 4 : i === 5 ? 4 : 4} style={{ height: '12px', display: 'block' }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(7)].map((_, j) => (
                    <td key={j} className="py-3">
                      <Placeholder as="span" animation="glow" xs={j === 0 ? 2 : j === 1 ? 8 : j === 2 ? 6 : j === 3 ? 6 : j === 4 ? 8 : j === 5 ? 6 : 4} style={{ height: '10px', display: 'block' }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card.Body>
    </Card>
  );

  const getPerformanceColor = (performance) => {
    if (performance >= 100) return 'success';
    if (performance >= 70) return 'info';
    if (performance >= 50) return 'warning';
    return 'danger';
  };

  const getPerformanceIcon = (performance) => {
    if (performance >= 100) return <Trophy className="text-warning" size={18} />;
    if (performance >= 70) return <StarFill className="text-info" size={16} />;
    if (performance >= 50) return <Star className="text-warning" size={16} />;
    return <ArrowDown className="text-danger" size={16} />;
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="text-warning" size={20} />;
    if (index === 1) return <Award className="text-secondary" size={20} />;
    if (index === 2) return <Award className="text-info" size={20} />;
    return <span className="fw-bold text-muted">{index + 1}</span>;
  };

  // ✅ Si l'utilisateur n'a pas les droits
  if (!canViewPerformance) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
          <Container fluid>
            <Card className="border-0 shadow-sm rounded-3 text-center p-5">
              <div className="mb-4">
                <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ width: 80, height: 80 }}>
                  <Lock size={40} className="text-danger" />
                </div>
              </div>
              <h4 className="fw-bold text-danger">Accès non autorisé</h4>
              <p className="text-muted mb-4 max-width-500 mx-auto">
                Vous n'avez pas les droits nécessaires pour accéder à la performance des agents.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  <ArrowLeft className="me-2" size={16} />
                  Retour au tableau de bord
                </Button>
              </div>
            </Card>
          </Container>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // ✅ Si l'API retourne une erreur 403
  if (error && error.includes("Accès non autorisé")) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
          <Container fluid>
            <Card className="border-0 shadow-sm rounded-3 text-center p-5">
              <div className="mb-4">
                <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ width: 80, height: 80 }}>
                  <Lock size={40} className="text-danger" />
                </div>
              </div>
              <h4 className="fw-bold text-danger">Accès non autorisé</h4>
              <p className="text-muted mb-4 max-width-500 mx-auto">
                {error}
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  Retour au tableau de bord
                </Button>
                <Button variant="outline-secondary" onClick={() => window.location.reload()}>
                  <Repeat className="me-2" size={16} />
                  Réessayer
                </Button>
              </div>
            </Card>
          </Container>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // ✅ Rendu principal
  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <Container fluid>
          {/* En-tête */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary bg-opacity-10 p-3 rounded-3">
                  <BarChart size={32} className="text-primary" />
                </div>
                <div>
                  <h4 className="main-title mb-0 fw-bold" style={{ fontSize: '24px' }}>
                    Performance des Agents
                  </h4>
                  <p className="text-muted mb-0 d-flex align-items-center gap-2">
                    <Calendar size={14} />
                    Vue d'ensemble des performances
                    <span className="text-muted">•</span>
                    {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2 flex-wrap mt-3 mt-md-0">
              <ButtonGroup size="sm" className="shadow-sm">
                <Button 
                  variant={timeRange === "week" ? "primary" : "outline-primary"} 
                  onClick={() => setTimeRange("week")}
                  className="px-3"
                  disabled={loading}
                >
                  Semaine
                </Button>
                <Button 
                  variant={timeRange === "month" ? "primary" : "outline-primary"} 
                  onClick={() => setTimeRange("month")}
                  className="px-3"
                  disabled={loading}
                >
                  Mois
                </Button>
                <Button 
                  variant={timeRange === "year" ? "primary" : "outline-primary"} 
                  onClick={() => setTimeRange("year")}
                  className="px-3"
                  disabled={loading}
                >
                  Année
                </Button>
              </ButtonGroup>

              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="shadow-sm"
                onClick={() => loadData(true)} 
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : <Repeat size={16} />}
              </Button>
            </div>
          </div>

          {/* Erreur */}
          {error && !error.includes("Accès non autorisé") && (
            <Alert variant="danger" className="border-0 shadow-sm d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <ExclamationCircle size={24} className="me-2" />
                <span className="fw-medium">{error}</span>
              </div>
              <Button variant="outline-danger" size="sm" onClick={() => loadData(true)}>
                <Repeat className="me-1" size={14} /> Réessayer
              </Button>
            </Alert>
          )}

          {/* Statistiques Globales avec Skeleton */}
          <Row className="g-3 mb-4">
            {loading ? (
              <>
                <Col md={6} lg={3}><SkeletonStatCard /></Col>
                <Col md={6} lg={3}><SkeletonStatCard /></Col>
                <Col md={6} lg={3}><SkeletonStatCard /></Col>
                <Col md={6} lg={3}><SkeletonStatCard /></Col>
              </>
            ) : computedStats && (
              <>
                <Col md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100 stats-card">
                    <div className="position-absolute top-0 start-0 w-100 bg-primary" style={{ height: '4px' }} />
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                          <People size={24} className="text-primary" />
                        </div>
                        <div>
                          <h3 className="fw-bold mb-0">{computedStats.totalAgents}</h3>
                          <span className="text-muted small fw-medium">Agents actifs</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100 stats-card">
                    <div className="position-absolute top-0 start-0 w-100 bg-success" style={{ height: '4px' }} />
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: 'rgba(46, 204, 113, 0.1)' }}>
                          <Cash size={24} className="text-success" />
                        </div>
                        <div>
                          <h3 className="fw-bold mb-0">{formatCurrency(computedStats.totalCollecte)}</h3>
                          <span className="text-muted small fw-medium">Collecte totale</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100 stats-card">
                    <div className="position-absolute top-0 start-0 w-100 bg-info" style={{ height: '4px' }} />
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: 'rgba(13, 202, 240, 0.1)' }}>
                          <Bullseye size={24} className="text-info" />
                        </div>
                        <div>
                          <h3 className="fw-bold mb-0">{computedStats.tauxRecouvrement}%</h3>
                          <span className="text-muted small fw-medium">Taux de recouvrement</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3}>
                  <Card className="border-0 shadow-sm h-100 stats-card">
                    <div className="position-absolute top-0 start-0 w-100 bg-warning" style={{ height: '4px' }} />
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center">
                        <div className="rounded-3 p-2 me-3" style={{ backgroundColor: 'rgba(243, 156, 18, 0.1)' }}>
                          <GraphUp size={24} className="text-warning" />
                        </div>
                        <div>
                          <h3 className="fw-bold mb-0">{computedStats.moyennePerformance}%</h3>
                          <span className="text-muted small fw-medium">Performance moyenne</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </>
            )}
          </Row>

          {/* Podium des meilleurs agents */}
          {loading ? (
            // <Row className="g-3 mb-4">
            //   <Col lg={12}>
            //     <SkeletonPodium />
            //   </Col>
            // </Row>
            <></>
          ) : agents.length >= 1 && (
            // <Row className="g-3 mb-4">
            //   <Col lg={12}>
            //     <Card className="border-0 shadow-sm">
            //       <Card.Header className="bg-white border-bottom py-2">
            //         <div className="d-flex align-items-center">
            //           <Award size={16} className="text-warning me-2" />
            //           <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>
            //             Podium des meilleurs agents
            //           </Card.Title>
            //         </div>
            //       </Card.Header>
            //       <Card.Body className="py-3">
            //         <Row className="align-items-end g-2">
            //           {/* 2ème place */}
            //           {agents.length >= 2 && (
            //             <Col md={4} className="order-md-2 text-center">
            //               <div className="p-2 bg-secondary bg-opacity-10 rounded-3 border border-secondary">
            //                 <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mx-auto mb-1" style={{ width: '36px', height: '36px' }}>
            //                   <span className="text-white fw-bold" style={{ fontSize: '14px' }}>2</span>
            //                 </div>
            //                 <h6 className="fw-bold mb-0" style={{ fontSize: '12px' }}>{agents[1]?.nom || 'Agent'}</h6>
            //                 <Badge bg="secondary" className="mb-1" style={{ fontSize: '9px' }}>2ème</Badge>
            //                 <div className="text-success fw-bold" style={{ fontSize: '11px' }}>{formatCurrency(agents[1]?.collecte || 0)}</div>
            //                 <ProgressBar now={agents[1]?.performance || 0} variant="secondary" style={{ height: '4px' }} className="mt-1" />
            //                 <small className="text-muted" style={{ fontSize: '9px' }}>{agents[1]?.performance || 0}%</small>
            //               </div>
            //             </Col>
            //           )}
            //           {/* 1ère place */}
            //           <Col md={4} className="order-md-1 text-center">
            //             <div className="p-2 bg-warning bg-opacity-10 rounded-3 border border-warning">
            //               <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center mx-auto mb-1" style={{ width: '42px', height: '42px' }}>
            //                 <Trophy size={20} className="text-white" />
            //               </div>
            //               <h6 className="fw-bold mb-0" style={{ fontSize: '13px' }}>{agents[0]?.nom || 'Agent'}</h6>
            //               <Badge bg="warning" className="mb-1" style={{ fontSize: '9px' }}>1er</Badge>
            //               <div className="text-success fw-bold" style={{ fontSize: '11px' }}>{formatCurrency(agents[0]?.collecte || 0)}</div>
            //               <ProgressBar now={agents[0]?.performance || 0} variant="warning" style={{ height: '4px' }} className="mt-1" />
            //               <small className="text-muted" style={{ fontSize: '9px' }}>{agents[0]?.performance || 0}%</small>
            //             </div>
            //           </Col>
            //           {/* 3ème place */}
            //           {agents.length >= 3 && (
            //             <Col md={4} className="order-md-3 text-center">
            //               <div className="p-2 bg-info bg-opacity-10 rounded-3 border border-info">
            //                 <div className="rounded-circle bg-info d-flex align-items-center justify-content-center mx-auto mb-1" style={{ width: '36px', height: '36px' }}>
            //                   <span className="text-white fw-bold" style={{ fontSize: '14px' }}>3</span>
            //                 </div>
            //                 <h6 className="fw-bold mb-0" style={{ fontSize: '12px' }}>{agents[2]?.nom || 'Agent'}</h6>
            //                 <Badge bg="info" className="mb-1" style={{ fontSize: '9px' }}>3ème</Badge>
            //                 <div className="text-success fw-bold" style={{ fontSize: '11px' }}>{formatCurrency(agents[2]?.collecte || 0)}</div>
            //                 <ProgressBar now={agents[2]?.performance || 0} variant="info" style={{ height: '4px' }} className="mt-1" />
            //                 <small className="text-muted" style={{ fontSize: '9px' }}>{agents[2]?.performance || 0}%</small>
            //               </div>
            //             </Col>
            //           )}
            //         </Row>
            //       </Card.Body>
            //     </Card>
            //   </Col>
            // </Row>
            <></>
          )}

          {/* Graphique et tableau */}
          <Row className="g-4 mb-4">
            <Col lg={12}>
              {loading ? (
                <SkeletonChart />
              ) : (
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                    <div>
                      <Card.Title as="h6" className="mb-0 fw-semibold">
                        <BarChart className="me-2 text-primary" size={18} />
                        Comparaison des performances
                      </Card.Title>
                      <small className="text-muted">Collecté vs Objectif par agent</small>
                    </div>
                    <div className="d-flex gap-2">
                      <InputGroup size="sm" style={{ width: '250px' }}>
                        <InputGroup.Text><Search size={14} /></InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher un agent..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {agents.length > 0 ? (
                      <>
                        <ReactApexChart 
                          options={chartOptions} 
                          series={chartSeries} 
                          type="bar" 
                          height={400} 
                        />
                        
                        <div className="mt-4">
                          <Table hover responsive className="mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="py-3" style={{ width: '50px' }}>#</th>
                                <th className="py-3" style={{ cursor: 'pointer' }} onClick={() => handleSort('nom')}>
                                  Agent
                                  {sortField === 'nom' && <span className="ms-1">{sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</span>}
                                </th>
                                <th className="py-3 text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('collecte')}>
                                  Collecté
                                  {sortField === 'collecte' && <span className="ms-1">{sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</span>}
                                </th>
                                <th className="py-3 text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('objectif')}>
                                  Objectif
                                  {sortField === 'objectif' && <span className="ms-1">{sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</span>}
                                </th>
                                <th className="py-3 text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('performance')}>
                                  Performance
                                  {sortField === 'performance' && <span className="ms-1">{sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</span>}
                                </th>
                                <th className="py-3 text-center">Statut</th>
                                <th className="py-3 text-center">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredAndSortedAgents.map((agent, index) => (
                                <tr key={agent.id || index} className="align-middle">
                                  <td className="text-center">{getRankIcon(index)}</td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-2" style={{ width: '36px', height: '36px' }}>
                                        <Person size={16} className="text-primary" />
                                      </div>
                                      <div>
                                        <div className="fw-medium">{agent.nom || 'Agent'}</div>
                                        <small className="text-muted d-block">{agent.role || 'Agent'}</small>
                                        {agent.id_marche && (
                                          <Badge bg="info" className="d-flex align-items-center px-2 py-1 rounded-pill mt-1" style={{ fontSize: '8px' }}>
                                            <Shop className="me-1" size={8} /> Marché
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-end text-success fw-bold">{formatCurrency(agent.collecte || 0)}</td>
                                  <td className="text-end text-muted">{formatCurrency(agent.objectif || 0)}</td>
                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <ProgressBar 
                                        now={agent.performance || 0} 
                                        variant={getPerformanceColor(agent.performance || 0)} 
                                        style={{ width: '80px', height: '6px' }} 
                                      />
                                      <span className={`fw-bold text-${getPerformanceColor(agent.performance || 0)}`}>
                                        {agent.performance || 0}%
                                      </span>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    {getPerformanceIcon(agent.performance || 0)}
                                    <span className="ms-1 small">
                                      {(agent.performance || 0) >= 100 ? 'Excellent' :
                                       (agent.performance || 0) >= 70 ? 'Très bon' :
                                       (agent.performance || 0) >= 50 ? 'Bon' :
                                       'À améliorer'}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <Button 
                                      variant="outline-primary" 
                                      size="sm" 
                                      onClick={() => handleAgentClick(agent)}
                                      title="Voir détails"
                                    >
                                      <Eye size={14} />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          {filteredAndSortedAgents.length === 0 && (
                            <div className="text-center py-4">
                              <ExclamationCircle size={48} className="text-muted opacity-25 mb-3" />
                              <p className="text-muted">Aucun agent trouvé</p>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5">
                        <ExclamationCircle size={48} className="text-muted opacity-25 mb-3" />
                        <p className="text-muted">Aucune donnée de performance disponible</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>

          {/* Modal détails agent */}
          <Modal show={showAgentModal} onHide={() => setShowAgentModal(false)} centered size="lg">
            <Modal.Header closeButton className="border-0 text-white" style={{ background: 'linear-gradient(135deg, #0d6efd, #0b5ed7)' }}>
              <Modal.Title>
                <Person className="me-2" size={20} />
                Détails de l'agent
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
              {selectedAgent && (
                <>
                  <div className="text-center mb-4">
                    <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mx-auto" style={{ width: '80px', height: '80px' }}>
                      <Person size={40} className="text-primary" />
                    </div>
                    <h4 className="fw-bold mt-2">{selectedAgent.nom || 'Agent'}</h4>
                    <Badge bg="secondary" className="rounded-pill">{selectedAgent.role || 'Agent'}</Badge>
                    {selectedAgent.id_marche && (
                      <Badge bg="info" className="ms-2 rounded-pill">
                        <Shop className="me-1" size={12} /> Marché
                      </Badge>
                    )}
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <div className="p-3 bg-white rounded-3 shadow-sm">
                        <small className="text-muted d-block">Collecte totale</small>
                        <h5 className="text-success mb-0">{formatCurrency(selectedAgent.collecte || 0)}</h5>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="p-3 bg-white rounded-3 shadow-sm">
                        <small className="text-muted d-block">Objectif</small>
                        <h5 className="text-primary mb-0">{formatCurrency(selectedAgent.objectif || 0)}</h5>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="p-3 bg-white rounded-3 shadow-sm">
                        <small className="text-muted d-block">Performance</small>
                        <h5 className={`mb-0 text-${getPerformanceColor(selectedAgent.performance || 0)}`}>
                          {selectedAgent.performance || 0}%
                        </h5>
                        <ProgressBar 
                          now={selectedAgent.performance || 0} 
                          variant={getPerformanceColor(selectedAgent.performance || 0)} 
                          style={{ height: '6px' }} 
                          className="mt-1"
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="p-3 bg-white rounded-3 shadow-sm">
                        <small className="text-muted d-block">Statut</small>
                        <h5 className="mb-0">
                          <Badge bg={(selectedAgent.performance || 0) >= 100 ? 'success' : (selectedAgent.performance || 0) >= 70 ? 'info' : (selectedAgent.performance || 0) >= 50 ? 'warning' : 'danger'}>
                            {(selectedAgent.performance || 0) >= 100 ? '🏆 Excellent' :
                             (selectedAgent.performance || 0) >= 70 ? '⭐ Très bon' :
                             (selectedAgent.performance || 0) >= 50 ? '👍 Bon' :
                             '📈 À améliorer'}
                          </Badge>
                        </h5>
                      </div>
                    </Col>
                  </Row>

                  {selectedAgent.details && (
                    <div className="mt-3 p-3 bg-white rounded-3 shadow-sm">
                      <h6 className="fw-semibold mb-2">Détails supplémentaires</h6>
                      <Row className="g-2">
                        <Col sm={6}>
                          <small className="text-muted d-block">Email</small>
                          <span>{selectedAgent.details.email || 'Non spécifié'}</span>
                        </Col>
                        <Col sm={6}>
                          <small className="text-muted d-block">Téléphone</small>
                          <span>{selectedAgent.details.telephone || 'Non spécifié'}</span>
                        </Col>
                      </Row>
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowAgentModal(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal>

          <Footer />
        </Container>
      </div>

      <style>{`
        .stats-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border-radius: 12px;
          overflow: hidden;
        }
        .stats-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.08) !important;
        }
        .table > :not(caption) > * > * {
          padding: 12px 16px;
        }
        .table-hover tbody tr:hover {
          background-color: rgba(13, 110, 253, 0.05);
        }
        .btn-group .btn {
          border-radius: 6px !important;
        }
        .btn-group .btn:not(:first-child) {
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
        }
        .btn-group .btn:not(:last-child) {
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }
        .main-title {
          font-weight: 700;
        }
        .card {
          border-radius: 12px !important;
        }
        .shadow-sm {
          box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important;
        }
        .max-width-500 {
          max-width: 500px;
        }
      `}</style>
    </React.Fragment>
  );
}